import "./hamburger.css"

import EventEmitter from "eventemitter3";
import { EventTypes } from "../../../core/event-bus";
import { LoyaltyProvider, Modals, Pages } from "../../../types/enums";
import { Util } from "../../../utils/util"
import { DataLayerService } from '../../../services/data-layer-service';
import { ProfileService } from "../../../services/profile-service";
import { Data } from "../../../models/data";
import { Modal } from "../modal";
import { Context } from "../../../models/context";
import { LocalizationService } from "../../../services/localization-service";

export class Hamburger extends Modal {
    private readonly _data: Data;
    private readonly _profileService: ProfileService;

    private _ellipse: HTMLElement | null = null;
    private readonly _menuHome: HTMLElement | null = null;
    private readonly _menuLocations: HTMLElement | null = null;
    private _menuItems: HTMLElement | null = null;
    private _menuAccount: HTMLElement | null = null;
    private _menuFavorites: HTMLElement | null = null;
    private _menuOrder: HTMLElement | null = null;
    private _menuRewards: HTMLElement | null = null;
    private _buttonLogin: HTMLElement | null = null;
    private _loyalty: HTMLElement | null = null;
    private _loyaltyGuest: HTMLElement | null = null;
    private _loyaltyAccount: HTMLElement | null = null;
    private _loyaltyName: HTMLElement | null = null;
    private _loyaltyPoints: HTMLElement | null = null;
    private _loyaltyPointsValue: HTMLElement | null = null;
    private _loyaltyPointsText: HTMLElement | null = null;
    private _buttonLogout: HTMLElement | null = null;
    private _buttonSignup: HTMLElement | null = null;
    private _selectLanguage: HTMLInputElement | null = null;
    private _footer: HTMLElement | null = null;
    private readonly _menuTermsOfUse: HTMLElement | null = null;
    private readonly _menuPrivacyPolicy: HTMLElement | null = null;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, profileService: ProfileService, dataLayer: DataLayerService) {
        super("modal_hamburger", eventBus, context, dataLayer, localizationService, { hasCloseButton: true, hasCloseOnBlur: true, ariaLabel: "Navigation Menu" });

        this._data = data;
        this._profileService = profileService;

        this._menuItems = document.querySelector("#modal_hamburger #hamburger_items");
        this._menuHome = document.querySelector("#modal_hamburger [data-page=LANDING]");
        this._menuLocations = document.querySelector("#modal_hamburger [data-page=LOCATIONS]");
        this._menuAccount = document.querySelector("#modal_hamburger [data-page=ACCOUNT]");
        this._menuFavorites = document.querySelector("#modal_hamburger [data-page=FAVORITES]");
        this._menuOrder = document.querySelector("#modal_hamburger [data-page=ORDERS]");
        this._menuRewards = document.querySelector("#modal_hamburger [data-page=REWARDS]");
        this._loyalty = document.querySelector("#modal_hamburger #hamburger_loyalty");
        this._loyaltyGuest = document.querySelector("#modal_hamburger #hamburger_loyalty_guest");
        this._loyaltyAccount = document.querySelector("#modal_hamburger #hamburger_loyalty_account");
        this._loyaltyName = document.querySelector("#modal_hamburger #hamburger_loyalty_account_name");
        this._loyaltyPoints = document.querySelector("#modal_hamburger #hamburger_loyalty_points")
        this._loyaltyPointsValue = document.querySelector("#modal_hamburger #hamburger_loyalty_points_value");
        this._loyaltyPointsText = document.querySelector("#modal_hamburger #hamburger_loyalty_points_text");
        this._footer = document.querySelector("#modal_hamburger #hamburger_footer");
        this._buttonLogin = document.querySelector("#modal_hamburger #hamburger_login");
        this._buttonSignup = document.querySelector("#modal_hamburger #hamburger_signup");
        this._buttonLogout = document.querySelector("#modal_hamburger #hamburger_logout");
        this._ellipse = document.querySelector("#modal_hamburger #hamburger_ellipse");
        this._selectLanguage = document.querySelector("#modal_hamburger #hamburger_language_select") as HTMLInputElement;
        this._menuTermsOfUse = document.querySelector("#modal_hamburger #hamburger_terms_of_use");
        this._menuPrivacyPolicy = document.querySelector("#modal_hamburger #hamburger_privacy_policy");

        this._eventBusListeners();
    }

    public init(): void {
        super.init();

        this._setEventListeners();

        if (Util.isAppView()) {
            this._footer && (this._footer.style.display = "none");
            this._updateExternalLinks();
        }

        this._profileService.user.isLoggedIn() ? this._updateLogin() : this._updateLogout();
    }

    public open(): void {
        this._updateEllipse();
        super.open();
    }

    private _eventBusListeners(): void {
        this._eventBus.on(EventTypes.LOGGED_IN, this._updateLogin);
        this._eventBus.on(EventTypes.LOGGED_OUT, this._updateLogout);
        this._eventBus.on(EventTypes.BRAND_DATA_RETRIEVED, this._onBrandDataGet);

        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, (language: string) => {
            this._selectLanguage && (this._selectLanguage.value = language);
        })
    }

    private readonly _updateLogout = (): void => {
        this._loyaltyGuest && (this._loyaltyGuest.style.display = "")
        this._loyaltyAccount && (this._loyaltyAccount.style.display = "none")
        this._buttonLogout && (this._buttonLogout.style.display = "none")
        this._menuAccount && (this._menuAccount.style.display = "none");
        this._menuFavorites && (this._menuFavorites.style.display = "none");
        this._menuOrder && (this._menuOrder.style.display = "none");

        this._updateEllipse();
    }

    private readonly _updateLogin = (): void => {
        this._loyaltyGuest && (this._loyaltyGuest.style.display = "none")
        this._loyaltyAccount && (this._loyaltyAccount.style.display = "")
        this._buttonLogout && (this._buttonLogout.style.display = "")
        this._menuAccount && (this._menuAccount.style.display = "");
        this._menuFavorites && (this._menuFavorites.style.display = "");
        this._menuOrder && (this._menuOrder.style.display = "");

        const userName = this?._profileService.user.getProperty("Name") || "";
        if (this._loyaltyName && this._loyaltyName.innerText != userName) {
            this._loyaltyName.innerText = userName;
        }

        if (this._loyaltyPointsValue && this?._profileService.user.getProperty("LoyaltyData")?.CurrentPoints) {
            this._loyaltyPointsValue.innerText = this?._profileService.user.getProperty("LoyaltyData")?.CurrentPoints.toString() || "";
        }

        if (this._loyaltyPointsText) {
            const pointsText = this._localizationService.names("AVAILABLE_POINTS_TEMPLATE")
                .replaceAll("{{points_unit}}", this._localizationService.getLoyaltyUnitName(this._data.getLoyaltyInfo()));
            this._loyaltyPointsText.innerText = pointsText;
        }

        this._updateEllipse();
    }

    private readonly _updateExternalLinks = (): void => {
        const settings = this._data.getBrandProperty("Settings")?.OOS;

        this._toggleExternalLink(this._menuTermsOfUse, settings?.appTermsOfUseUrl);
        this._toggleExternalLink(this._menuPrivacyPolicy, settings?.appPrivacyPolicyUrl);
    }

    private _toggleExternalLink(element: HTMLElement | null, url: string | undefined): void {
        if (!element) return;

        const trimmed = url?.trim();
        element.style.display = trimmed ? "" : "none";

        if (trimmed) {
            element.dataset.url = trimmed;
        } else {
            delete element.dataset.url;
        }
    }

    private readonly _openExternalLink = (event: Event): void => {
        const url = (event.currentTarget as HTMLElement)?.dataset.url;
        if (!url) return;

        if (Util.isAppView() && typeof openExternalLink == "function") {
            openExternalLink(url);
        } else {
            window.open(url, "_blank", "noopener,noreferrer");
        }

        this.close();
    }

    private readonly _updateEllipse = (): void => {
        const height = (this._loyalty?.clientHeight || 0) + 30;
        if (this._ellipse) {
            this._ellipse.style.height = `${height}px`;
        }
    }

    private _bindNavItem(element: Element, handler: (e: Event) => void): void {
        element.addEventListener("click", handler);
        Util.addKeyboardActivation(element as HTMLElement, handler);
    }

    protected _setEventListeners(): void {
        document.getElementById(this._elementId)?.addEventListener("click", (e: Event) =>
            e.stopPropagation()
        );

        const onNavigate = (event: Event) => {
            event.stopImmediatePropagation();
            const element = (event.target as HTMLElement)?.closest("[data-page]") as HTMLElement;
            if (!element) return;

            const page = Pages[element.dataset.page as keyof typeof Pages];
            this._eventBus.emit(
                EventTypes.LOCATION_CHANGE,
                page,
            )

            this.close();
        };

        this._menuItems?.querySelectorAll("[data-page]")?.forEach(
            (element: Element) => {
                this._bindNavItem(element, onNavigate);
            });
        
        this._menuTermsOfUse && this._bindNavItem(this._menuTermsOfUse, this._openExternalLink);
        this._menuPrivacyPolicy && this._bindNavItem(this._menuPrivacyPolicy, this._openExternalLink);

        this._buttonLogin?.addEventListener("click", () => {
            this.close();
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN)
        });

        this._buttonSignup?.addEventListener("click", () => {
            this.close();
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.SIGNUP)
        });

        this._buttonLogout?.addEventListener("click", async () => {
            await this._profileService.signOut();
            this._eventBus.emit(EventTypes.LOGGED_OUT);
        });

        this._selectLanguage?.addEventListener("change", (e: Event) => {
            this._eventBus.emit(EventTypes.LANGUAGE_CHANGED, (e.target as HTMLInputElement).value, true)
        });
    }

    private readonly _onBrandDataGet = async (): Promise<void> => {
        if (Util.isAppView()) {
            this._updateExternalLinks();
        }

        if (this._data.getLoyaltyInfo().providerId == LoyaltyProvider.NONE) {
            this._menuRewards && (this._menuRewards.style.display = "none");
            this._loyaltyPoints && (this._loyaltyPoints.style.display = "none");
        }

        if (this._footer) {
            const url = `${this._context.getProperty("publicStorageUrl")}/online3/online-brand-hamburger.html?v=${Date.now()}`;

            await fetch(url).then(r => {
                r.ok && r.text().then(html => {
                    if (!this._footer) return;
                    this._footer.innerHTML = html;
                    for (const a of Array.from(this._footer.querySelectorAll<HTMLAnchorElement>("a")))
                        a.setAttribute("role", "link");
                })
            });
        }

        // Website-only redirects (e.g. store locator) must not apply inside the app view — see
        // Header._setRedirectionUrls, which guards the same settings the same way.
        if (!Util.isAppView()) {
            const homepageRedirectUrl = this._data.getBrandProperty("Settings").OOS.homepageRedirectUrl;
            if (this._menuHome && homepageRedirectUrl) {
                this._menuHome.innerHTML = `<a href="${homepageRedirectUrl}"><span class="icon-home"></span><span ltag="Home">HOME</span></a>`;
                delete this._menuHome.dataset.page;
            }

            const locationsRedirectUrl = this._data.getBrandProperty("Settings").OOS.storeLocatorRedirectUrl;
            if (this._menuLocations && locationsRedirectUrl) {
                this._menuLocations.innerHTML = `<a href="${locationsRedirectUrl}"><span class="icon-map"></span><span ltag="FindStore">FIND A STORE</span></a>`;
                delete this._menuLocations.dataset.page;
            }
        }
    }
}